import { useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import style from "../EventsAudit/EventAuditList.module.css";

import { useAppSelector } from "../../shared/hook";
import Pagination from "../../shared/components/Table/Pagination";
import { EmptyListPlaceholderContent } from "../../shared/Ui/EmptyPageBlock";
import SelectCustomFormik from "../../shared/Ui/SelectCustom/SelectCustomFormik";
import PageNotRole from "../../shared/Ui/PageNotFound/PageNotRole";
import { getAccountsList } from "../../common/api/billingApi";
import { useProductGroupOptions } from "../../shared/hook/useProductGroupOptions";
import { canAccessLocalBilling, formatBillingAmount } from "./billingUtils";

const formatCurrency = formatBillingAmount;

type PaginationState = {
  pageIndex: number;
  pageSize: number;
};

function AccountsJournalList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const pageSize = useAppSelector((state) => state.userSettings.pageSize);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize,
  });
  const [tableData, setTableData] = useState<{
    currentPage?: number;
    itemsPerPage?: number;
    totalItems: number;
    totalPages: number;
  }>();
  const [values, setValues] = useState<any[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // Товарные группы с названием на языке интерфейса (фильтр — по коду ТГ)
  const productGroups = useProductGroupOptions();
  const productGroupOptions = productGroups.optionsByCode;
  const [initialValues, setInitialValues] = useState({
    orgIinBin: "",
    productGroupCode: "",
  });

  const hasLocalBillingAccess = canAccessLocalBilling(userData);

  const calculateItemNumber = (index: number) =>
    pagination.pageIndex * pagination.pageSize + index + 1;

  const loadList = async (page = pagination.pageIndex + 1) => {
    try {
      const response = await getAccountsList(
        page,
        pagination.pageSize,
        {
          orgIinBin: initialValues.orgIinBin || undefined,
          productGroupCode: initialValues.productGroupCode || undefined,
        },
      );

      const items =
        response?.items ??
        response?.accounts ??
        response?.content ??
        (Array.isArray(response) ? response : []);

      setValues(items);
      setTableData({
        currentPage: response?.currentPage ?? pagination.pageIndex + 1,
        itemsPerPage: response?.itemsPerPage ?? pagination.pageSize,
        totalItems: response?.totalItems ?? items.length,
        totalPages: response?.totalPages ?? 0,
      });
    } catch (error) {
      console.error("Error fetching accounts list:", error);
      setValues([]);
      setTableData({
        currentPage: pagination.pageIndex + 1,
        itemsPerPage: pagination.pageSize,
        totalItems: 0,
        totalPages: 0,
      });
    }
  };

  useEffect(() => {
    if (!hasLocalBillingAccess) return;
    loadList();
  }, [pagination, hasLocalBillingAccess]);

  const renderFilterInputs = () => (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={() => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setIsFilterOpen(false);
        loadList(1);
      }}
    >
      {(formik) => (
        <Form className="max-w-[1585px] w-full">
          <div className="flex flex-row align-center justify-between">
            <p className="my-3 text-lg font-semibold">
              {t("documents_upload.filters")}
            </p>
            <button type="reset" onClick={() => setIsFilterOpen(false)}>
              <img src="/icons/close.svg" alt="close" />
            </button>
          </div>
          <div className="flex flex-col">
            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("billing.orgIinBin")}
                </label>
              </div>
              <Field
                type="text"
                name="orgIinBin"
                placeholder={t("documentLabels.choose_option")}
                className={style.placeholder}
                value={formik.values.orgIinBin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formik.setFieldValue("orgIinBin", e.target.value);
                  setInitialValues((prev) => ({
                    ...prev,
                    orgIinBin: e.target.value,
                  }));
                }}
              />
            </div>
            <div className={style.inputField}>
              <div className={style.labelAndButtonWrapper}>
                <label className={style.inputLabel}>
                  {t("billing.productGroup")}
                </label>
              </div>
              <Field
                component={SelectCustomFormik}
                name="productGroupCode"
                placeholder={t("documentLabels.choose_option")}
                options={productGroupOptions}
                onChange={(selected: string) => {
                  setInitialValues((prev) => ({
                    ...prev,
                    productGroupCode: selected ?? "",
                  }));
                }}
              />
            </div>
          </div>
          <div className="flex flex-row gap-[10px] justify-end mt-2">
            <button
              type="submit"
              className="bg-green text-white font-semibold py-2 px-4 rounded"
            >
              {t("mainButtons3.apply")}
            </button>
            <button
              type="reset"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => {
                setInitialValues({ orgIinBin: "", productGroupCode: "" });
                formik.resetForm({
                  values: { orgIinBin: "", productGroupCode: "" },
                });
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
            >
              {t("mainButtons3.reset")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  if (!hasLocalBillingAccess) {
    return <PageNotRole />;
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">{t("billing.accountsJournalTitle")}</h2>
      <div className="bg-white my-5 p-5 pt-8 relative rounded-xl shadow-md flex flex-row items-center justify-between">
        <div className="flex-grow" />
        <div className="flex flex-row gap-[10px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsFilterOpen(true);
            }}
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            data-tooltip-offset={4}
            data-tooltip-id="billing-accounts-filter"
          >
            {t("mainButtons3.filter")}
          </button>
          <button type="button" onClick={() => loadList()}>
            <img src="/icons/update.svg" alt="update" />
          </button>
        </div>
      </div>
      <div className="w-full">
        <div className="rounded-lg overflow-x-auto bg-white">
          <table className="max-w-full w-full leading-normal">
            <thead className="border-b-2 border-gray-300">
              <tr>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  №
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.orgIinBin")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.orgName")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.accountNumber")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.productGroup")}
                </th>
                <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                  {t("billing.currentBalance")}
                </th>
              </tr>
            </thead>
            {values.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <EmptyListPlaceholderContent title={t("billing.noData")} />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="font-normal text-center">
                {values.map((value: any, index: number) => (
                  <tr key={`account-${value?.id ?? index}`}>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {calculateItemNumber(index)}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {value?.orgIinBin ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {value?.orgName ?? "-"}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {value?.accountNumber ? (
                        <button
                          type="button"
                          className="text-green underline"
                          onClick={() =>
                            navigate(`/billing/accounts/${value?.id}`)
                          }
                        >
                          {value.accountNumber}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {productGroups.nameByCode(value?.productGroupCode, value?.productGroupName)}
                    </td>
                    <td className="px-5 py-5 text-sm border-b border-gray-200 text-gray-600">
                      {formatCurrency(value?.currentBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        <Pagination
          totalItems={tableData?.totalItems || 0}
          totalPages={tableData?.totalPages || 0}
          pagination={pagination}
          setPagination={setPagination}
        />
      </div>
      <div className="z-[1000] relative">
        <Tooltip
          isOpen={isFilterOpen}
          id="billing-accounts-filter"
          className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
          openOnClick
          clickable
          noArrow
          opacity={1}
          place="bottom-end"
          afterShow={() => setIsFilterOpen(true)}
          afterHide={() => setIsFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>{renderFilterInputs()}</div>
        </Tooltip>
      </div>
    </div>
  );
}

export default AccountsJournalList;
